import { useRef, useState } from 'react'

import { Box, Button, Form } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/factorial-one-react/dist/experimental'
import { AtsFeedbackDetailFragment, AtsFileFragment } from 'generated/resources/graphql'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useForm } from 'modules/core/hooks/useForm'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RatingSelector } from 'modules/ats/components/Feedbacks/RatingSelector'
import { useUpdateFeedback } from 'modules/ats/hooks/feedbacks/useUpdateFeedback'
import { AtsFeedbackFormValues } from 'modules/ats/lib/utils/feedbacks'
import {
  useNotesMentions,
  useRichTextEditorNotesMentions,
} from 'modules/ats/lib/utils/useNotesMentions'

interface Props {
  feedback: AtsFeedbackDetailFragment
  onEditFinish: () => void
  enabledScoring?: boolean
}

export const FeedbackEdit = ({ feedback, onEditFinish, enabledScoring = true }: Props) => {
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()
  const { updateFeedback } = useUpdateFeedback(feedback)
  const editorRef = useRef<EditorHandle>(null)
  const [accessesIds, setAccessesIds] = useState<number[] | null>([])
  const { candidate, applicationPhase, atsFiles, application, description, rating } = feedback

  const uniqueMentioningAccesses = useNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId: candidate.id,
  })

  const {
    accesses: uniqueRichTextEditorMentioningAccesses,
    loading: richTextEditorMentionsLoading,
  } = useRichTextEditorNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId: candidate.id,
  })

  const toFile = (attachments: AtsFileFragment[]): File[] => {
    return attachments.map(
      (attachment) =>
        new File([], attachment.file.filename, {
          type: attachment.file.contentType || '',
          lastModified: new Date().getTime(),
        })
    )
  }

  const form = useForm<AtsFeedbackFormValues>({
    defaultValues: {
      description,
      rating,
      atsCandidateId: candidate.id,
      atsApplicationId: application?.id,
      atsApplicationPhaseId: applicationPhase?.id,
      mentionIds: accessesIds,
      attachments: atsFiles.length ? toFile(atsFiles) : [],
    },
    async onSubmit(data, { setErrors }) {
      const result = await updateFeedback({
        ...data,
        attachments: data.attachments && data.attachments.length > 0 ? data.attachments : null,
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
      onEditFinish()

      return handled
    },
  })

  return (
    <Form onSubmit={() => form.handleSubmit()} padding={null}>
      <Box gap="s8" flexDirection="row" alignItems="center" maxWidth="s600">
        <UserAvatar user={feedback.access ? feedback.access : undefined} size="s40" />
        {enabledScoring && (
          <RatingSelector
            value={Number(form.values.rating)}
            onChange={(rat) => form.setFieldValue('rating', rat)}
          />
        )}
      </Box>

      {richTextEditorLoading || richTextEditorMentionsLoading ? (
        <RichTextEditor.Skeleton />
      ) : canUseRichTextEditor ? (
        <RichTextEditor
          {...editorConfig}
          title={i18n.t('ats.feedbacks.title_edit')}
          onChange={(data) => {
            form.setFieldValue('description', data?.value)
            setAccessesIds(data?.mentionIds || null)
          }}
          placeholder={i18n.t('ats.feedbacks.placeholder')}
          maxCharacters={5000}
          initialEditorState={{
            content: form.values.description || undefined,
          }}
          mentionsConfig={{
            users: uniqueRichTextEditorMentioningAccesses,
          }}
          primaryAction={{
            action: {
              label: i18n.t('common.save'),
              onClick: () => form.handleSubmit(),
              variant: 'default',
            },
          }}
          secondaryAction={{
            label: i18n.t('common.cancel'),
            onClick: onEditFinish,
            variant: 'outline',
          }}
        />
      ) : (
        <>
          <TextEditor
            ref={editorRef}
            onChange={(data) => {
              if (data && typeof data !== 'string') {
                form.setFieldValue('description', data.value)
                setAccessesIds(data?.ids)
              }
            }}
            placeholder={i18n.t('ats.feedbacks.placeholder')}
            initialEditorState={{
              data: form.values.description || '',
            }}
            height="s200"
            hasMentions
            users={uniqueMentioningAccesses}
            enableBetaFeatures={false}
          />
          <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
            <Button type="secondary" disabled={form.isSubmitting} onClick={onEditFinish}>
              {i18n.t('common.cancel')}
            </Button>

            <Button disabled={form.isSubmitting || !form.isValid} submit>
              {i18n.t('common.save')}
            </Button>
          </Box>
        </>
      )}
    </Form>
  )
}
